/**
 * Company Hooks (yritysrekisteri — epic #763)
 *
 * TanStack Query hooks for the company registry and its person links.
 */

import { useQuery, useMutation, useQueryClient, useInfiniteQuery } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { queryKeys } from './queryKeys';
import { fromOffsetResponse, usePagedQuery } from './usePagedQuery';

export interface Company {
  id: string;
  name: string;
  businessId: string | null;
  phone: string | null;
  email: string | null;
  website: string | null;
  streetAddress: string | null;
  postalCode: string | null;
  city: string | null;
  country: string;
  billingStreetAddress: string | null;
  billingPostalCode: string | null;
  billingCity: string | null;
  invoiceEmail: string | null;
  eInvoiceAddress: string | null;
  ovtId: string | null;
  /** Absent from responses cached before #3066. */
  eInvoiceOperator?: string | null;
  paymentTermsDays: number | null;
  notes: string | null;
  laborRateId?: string | null;
  legacyCustomerId: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface CompanyPersonLink {
  id: string;
  personId: string;
  role: string | null;
  isPrimary: boolean;
  validFrom: string;
  validTo: string | null;
  firstName: string | null;
  lastName: string | null;
  email: string | null;
  phone: string | null;
}

interface PaginatedResponse<T> {
  data: T[];
  pagination: { nextCursor: string | null; prevCursor: string | null; hasMore: boolean };
}

export interface CompanyFilters {
  [key: string]: string | number | undefined;
  q?: string;
  sortBy?: 'name' | 'createdAt';
  sortOrder?: 'asc' | 'desc';
  limit?: number;
}

export interface BusinessIdMatch {
  company: Company;
  similarity: number | null;
  likelyTypo: boolean;
}

export interface BusinessIdLookupResult {
  company: Company | null;
  matches: BusinessIdMatch[];
}

export interface CreateCompanyData {
  name: string;
  businessId?: string | null;
  /** Set only after the operator confirms a new branch that shares a Y-tunnus. */
  allowDuplicateBusinessId?: boolean;
  phone?: string | null;
  email?: string | null;
  website?: string | null;
  streetAddress?: string | null;
  postalCode?: string | null;
  city?: string | null;
  country?: string;
  billingStreetAddress?: string | null;
  billingPostalCode?: string | null;
  billingCity?: string | null;
  invoiceEmail?: string | null;
  eInvoiceAddress?: string | null;
  ovtId?: string | null;
  eInvoiceOperator?: string | null;
  paymentTermsDays?: number | null;
  notes?: string | null;
  laborRateId?: string | null;
}

export type UpdateCompanyData = Partial<CreateCompanyData>;

export function useCompanies(filters: CompanyFilters = {}, options?: { enabled?: boolean }) {
  return useInfiniteQuery({
    queryKey: queryKeys.companies.list(filters),
    queryFn: ({ pageParam }) =>
      api.get<PaginatedResponse<Company>>('/companies', {
        params: { ...filters, cursor: pageParam },
      }),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (lastPage) =>
      lastPage.pagination.hasMore ? lastPage.pagination.nextCursor : undefined,
    staleTime: 30 * 1000,
    enabled: options?.enabled ?? true,
  });
}

/**
 * Page-number list for CompaniesPage (#2737).
 * `useCompanies` stays for pickers that still read the cursor list.
 */
export function usePagedCompanies(filters: { q?: string } = {}) {
  return usePagedQuery({
    queryKey: queryKeys.companies.lists(),
    filters,
    defaultSort: { sortBy: 'name', sortDir: 'asc' },
    staleTime: 30 * 1000,
    fetchPage: async ({ filters: pageFilters, limit, offset, sortBy, sortDir }) => {
      const params: Record<string, string | number | undefined> = { limit, offset };
      if (pageFilters.q) params['q'] = pageFilters.q;
      if (sortBy !== undefined) params['sortBy'] = sortBy;
      if (sortDir !== undefined) params['sortDir'] = sortDir;
      const response = await api.get<{ data: Company[]; pagination: { total: number } }>('/companies', {
        params,
      });
      return fromOffsetResponse(response);
    },
  });
}

/**
 * Companies in this tenant that already use `businessId`.
 *
 * `excludeId` drops the row being edited. A response that only has the legacy
 * single `company` field is treated as one match so an older API still warns.
 */
export async function lookupCompaniesByBusinessId(input: {
  businessId: string;
  name?: string;
  excludeId?: string;
}): Promise<BusinessIdLookupResult> {
  const businessId = input.businessId.trim();
  if (!businessId) return { company: null, matches: [] };

  const params: Record<string, string> = { businessId };
  if (input.name?.trim()) params['name'] = input.name.trim();
  if (input.excludeId) params['excludeId'] = input.excludeId;

  const result = await api.get<{
    company?: Company | null;
    matches?: BusinessIdMatch[];
  }>('/companies/by-business-id', { params });

  const rawMatches = Array.isArray(result.matches)
    ? result.matches
    : result.company
      ? [{ company: result.company, similarity: null, likelyTypo: false }]
      : [];

  const matches = rawMatches.filter((match) => match.company.id !== input.excludeId);
  return { company: matches[0]?.company ?? null, matches };
}

export function useCompany(id: string | undefined) {
  return useQuery({
    queryKey: queryKeys.companies.detail(id ?? ''),
    queryFn: () => api.get<Company>(`/companies/${id}`),
    enabled: !!id,
    staleTime: 60 * 1000,
  });
}

export function useCompanyPersonLinks(companyId: string | undefined) {
  return useQuery({
    queryKey: queryKeys.companies.personLinks(companyId ?? ''),
    queryFn: () => api.get<{ data: CompanyPersonLink[] }>(`/companies/${companyId}/person-links`),
    enabled: !!companyId,
    staleTime: 60 * 1000,
  });
}

export function useCreateCompany() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: CreateCompanyData) => api.post<Company>('/companies', data),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.companies.lists() });
    },
  });
}

export function useUpdateCompany() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateCompanyData }) =>
      api.patch<Company>(`/companies/${id}`, data),
    onSuccess: (_, { id }) => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.companies.detail(id) });
      void queryClient.invalidateQueries({ queryKey: queryKeys.companies.lists() });
    },
  });
}

export function useDeleteCompany() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.delete(`/companies/${id}`),
    onSuccess: (_, id) => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.companies.detail(id) });
      void queryClient.invalidateQueries({ queryKey: queryKeys.companies.lists() });
    },
  });
}

export function useCreateCompanyPersonLink() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      companyId,
      data,
    }: {
      companyId: string;
      data:
        | { personId: string; role?: string | null; isPrimary?: boolean }
        | {
            firstName: string;
            lastName: string;
            phone?: string | null;
            email?: string | null;
            role?: string | null;
            isPrimary?: boolean;
          };
    }) => api.post<CompanyPersonLink>(`/companies/${companyId}/person-links`, data),
    onSuccess: (_, { companyId }) => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.companies.personLinks(companyId) });
    },
  });
}

export function useUpdateCompanyPersonLink() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      companyId,
      linkId,
      data,
    }: {
      companyId: string;
      linkId: string;
      data: { role?: string | null; isPrimary?: boolean };
    }) => api.patch<CompanyPersonLink>(`/companies/${companyId}/person-links/${linkId}`, data),
    onSuccess: (_, { companyId }) => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.companies.personLinks(companyId) });
    },
  });
}

export function useRemoveCompanyPersonLink() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ companyId, linkId }: { companyId: string; linkId: string }) =>
      api.delete(`/companies/${companyId}/person-links/${linkId}`),
    onSuccess: (_, { companyId }) => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.companies.personLinks(companyId) });
    },
  });
}
